import { Temporal } from '@js-temporal/polyfill'
import z from 'zod'

/** 0 = Sunday, 1 = Monday, … 6 = Saturday (cron-style; no duplicate 7 for Sunday). */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

/** Zod schema for DayOfWeek. */
export const dayOfWeekSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
])

/** Time of day: hour 0–23, minute 0–59. */
export interface TimeOfDay {
  hour: number
  minute: number
}

/** Zod schema for TimeOfDay. */
export const timeOfDaySchema = z.object({
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59)
})

/**
 * A recurring time window: active on the given days between start and end (inclusive of start, exclusive of end).
 * Start should be before end within the same day (no overnight ranges).
 * Optional startDate/endDate restrict the block to a calendar date range (inclusive on both ends).
 */
export interface ITimeBlock {
  id: string
  title: string
  description?: string
  /** Days when the block is active (0 = Sun … 6 = Sat). */
  days: DayOfWeek[]
  /** Start of the block (e.g. 09:00). */
  start?: TimeOfDay
  /** End of the block (e.g. 17:00). */
  end?: TimeOfDay
}

/** Zod schema for ITimeBlock. */
export const timeBlockSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  days: z.array(dayOfWeekSchema),
  start: timeOfDaySchema.optional(),
  end: timeOfDaySchema.optional()
})

/** Minutes since midnight for a TimeOfDay. */
function timeOfDayToMinutes(t: TimeOfDay): number {
  return t.hour * 60 + t.minute
}

const MINUTES_PER_DAY = 24 * 60

/**
 * Map Temporal dayOfWeek (1=Mon … 7=Sun) to our DayOfWeek (0=Sun … 6=Sat).
 */
function temporalDayToDayOfWeek(temporalDay: number): DayOfWeek {
  return temporalDay === 7 ? 0 : (temporalDay as DayOfWeek)
}

/**
 * Returns true if the reference date/time is included in at least one of the given time blocks.
 * Reference is interpreted in UTC (day of week and time of day are taken in UTC).
 * A date is included when: its UTC day of week is in the block's days, and its UTC time of day
 * is within the block's start–end window (inclusive of start, exclusive of end).
 * When start or end are missing: start defaults to 00:00, end defaults to 24:00 (end of day).
 * When both are missing, the block is considered active for the entire day on the given days.
 * When blocks is empty: the playlist is considered always running (returns true).
 */
export function isDateIncludedInTimeBlocks(
  reference: Temporal.Instant | string,
  blocks: ITimeBlock[]
): boolean {
  if (blocks.length === 0) {
    return true
  }
  const instant = typeof reference === 'string'
    ? Temporal.Instant.from(reference)
    : reference
  const zoned = instant.toZonedDateTimeISO('UTC')
  const dayOfWeek = temporalDayToDayOfWeek(zoned.dayOfWeek)
  const refMinutes = zoned.hour * 60 + zoned.minute

  return blocks.some((block) => {
    if (!block.days.includes(dayOfWeek)) {
      return false
    }
    const startMinutes = block.start != null ? timeOfDayToMinutes(block.start) : 0
    const endMinutes = block.end != null ? timeOfDayToMinutes(block.end) : MINUTES_PER_DAY
    if (refMinutes < startMinutes || refMinutes >= endMinutes) {
      return false
    }
    return true
  })
}
